import type { components } from './schema';

export type ErrorEnvelope = components['schemas']['ErrorResponseDto'];

/**
 * Every non-2xx response carries the same envelope (CLAUDE.md §5):
 * `{ code, message, details }`. The UI switches on `code` and never parses
 * `message` — the message is reworded freely, the code is contract.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  /**
   * Refine's `HttpError` reads `statusCode`. Mirroring `status` onto it lets an
   * `ApiError` travel through Refine's hooks unmodified instead of being
   * flattened into a plain object on the way and losing `code`.
   */
  readonly statusCode: number;

  constructor(status: number, envelope: Partial<ErrorEnvelope> | undefined) {
    super(envelope?.message ?? 'The request failed.');
    this.name = 'ApiError';
    this.status = status;
    this.statusCode = status;
    this.code = envelope?.code ?? fallbackCode(status);
    this.details = envelope?.details ?? {};
  }

  /**
   * Field-level messages for a form, from `details.fields`. Returns an empty
   * object when the API sent something else — a raw JSON dump under an input is
   * worse than no message at all.
   */
  get fieldErrors(): Record<string, string> {
    const raw = this.details['fields'];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

    const out: Record<string, string> = {};
    for (const [field, message] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof message === 'string') out[field] = message;
      else if (Array.isArray(message) && typeof message[0] === 'string') out[field] = message[0];
    }
    return out;
  }
}

function fallbackCode(status: number): string {
  if (status === 0) return 'NETWORK_ERROR';
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  return 'UNEXPECTED_ERROR';
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * A route the API has not built yet answers `501 NOT_IMPLEMENTED`; a route that
 * was never registered answers `404`. Both mean "this screen is waiting on the
 * backend", and the screens distinguish that from a genuine failure so an admin
 * is not sent to look for a fault that does not exist.
 */
export function isUnbuiltEndpoint(err: unknown): boolean {
  return isApiError(err) && (err.code === 'NOT_IMPLEMENTED' || err.status === 501);
}

/** Human-readable text for the codes the panel can actually provoke. */
const MESSAGES: Record<string, string> = {
  NETWORK_ERROR: 'Could not reach the API. Check your connection and try again.',
  UNAUTHENTICATED: 'Your session has ended. Sign in again.',
  INSUFFICIENT_ROLE: 'Your role does not allow this action.',
  FORBIDDEN: 'Your role does not allow this action.',
  MFA_REQUIRED: 'Enter the code from your authenticator app.',
  MFA_INVALID: 'That code is not valid. Codes expire after 30 seconds.',
  LAST_SUPER_ADMIN: 'This is the last active super admin. Promote someone else first.',
  ADMIN_ALREADY_EXISTS: 'An admin already exists with that email address.',
  INVITE_EXPIRED: 'This invitation has expired. Ask a super admin to send a new one.',
  INVITE_ALREADY_USED: 'This invitation has already been used. Sign in instead.',
  OPTION_IN_USE: 'This is in use by existing profiles. Deactivate it instead of deleting it.',
  WIZARD_ALREADY_PUBLISHED: 'Published versions cannot be edited. Create a new draft.',
  RATE_LIMITED: 'Too many attempts. Wait a moment and try again.',
};

export function messageFor(err: unknown): string {
  if (!isApiError(err)) return 'Something went wrong. Try again.';
  return MESSAGES[err.code] ?? err.message;
}
